import React, { useState, useRef, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import swal from "sweetalert";
import { useHistory } from "react-router-dom";
import commoncss from "../../css/common.module.css";
import { ReactComponent as PlusIcon } from "../../assets/icons/AddPlus.svg";
import { ReactComponent as ExportIcon } from "../../assets/icons/download.svg";
import { ReactComponent as CirclePlus } from "../../assets/icons/circlePlus.svg";
import { ReactComponent as AddEditor } from "../../assets/icons/addEditor.svg";
import { ReactComponent as AddTable } from "../../assets/icons/addTable.svg";
import libcss from "../../css/library.module.css";
import {
  createSocieApiHandler,
  createSubSocieApiHandler,
  updateSocieApiHandler,
  updateSubSocieApiHandler,
  getTemplateDetailsApiHandler,
} from "../../../WorkSpace/Components/ApiServices/apiHandler";
import { ReactComponent as EditIcon } from "../../../myfin_v2/assets/icons/editPri.svg";
import { ReactComponent as CloseIcon } from "../../../myfin_v2/assets/icons/close.svg";
import { ReactComponent as DelIcon } from "../../../myfin_v2/assets/icons/delete_red.svg";
import ControlledTableGenerator from "./TemplateTableGenerator";
import QuillReactEditor from "../../components/QuillReactEditor";
import SidePanel from "./SidePanel.jsx";
import DeletePopup from "../../elements/DeletePopup";

const genId = () => Math.random().toString(36).slice(2);

const normalizeFromInitial = (initialDoc) => {
  const items = [];
  (initialDoc?.data || []).forEach((block) => {
    if (block.dataType === "text") {
      items.push({
        id: genId(),
        type: "text",
        dataType: "text",
        textFormat: "html",
        textRaw: block?.textRaw ?? " ",
        textPlain: block?.textPlain ?? " ",
        text: [
          {
            id: genId(),
            content: block?.textRaw ?? " ",
          },
        ],
      });
    } else if (block.dataType === "table") {
      items.push({
        id: genId(),
        _id: block._id,
        type: "table",
        tableTitle: block.tableTitle || "Untitled Table",
        tableRow: Array.isArray(block.tableRow)
          ? block.tableRow.map((r) => ({ ...r }))
          : [],
        tableColumn: Array.isArray(block.tableColumn)
          ? block.tableColumn.map((c) => ({ ...c }))
          : [],
        tableType: block?.tableType ?? "table",
        table: block?.table ?? null,
        hasCellData: block?.tableType === "content",
      });
    }
  });
  return items;
};

// Serialize the flat list back to your exact schema
const serialize = (name, items) => {
  const data = items.map((it) => {
    if (it.type === "text") {
      return {
        dataType: "text",
        textFormat: it.textFormat,
        textRaw: it?.textRaw ?? " ",
        textPlain: it?.textPlain ?? " ",
      };
    }
    if (it.type === "table") {
      const hasCellData =
        it.hasCellData !== undefined
          ? it.hasCellData
          : (it.tableType === "content" || (Array.isArray(it.table) && it.table.length > 0));

      const tableObj = {
        dataType: "table",
        tableTitle: it.tableTitle || "Untitled Table",
        tableRow: (it.tableRow || []).map((row) => ({
  name: row.name || "",
  lineType: row.lineType || "line item",

  ...(row._id && { _id: row._id }),

  ...(row.formulaType && {
    formulaType: row.formulaType,
  }),

  ...(row.isBold !== undefined && {
    isBold: row.isBold,
  }),

  ...(row.lineType === "formula" && {
    formula: row.formula || [],
  }),
})),
        tableColumn: (it.tableColumn || []).map((col) => ({
          name: col.name || "",
          lineType: col.lineType || "line item",
          ...(col._id && { _id: col._id }),
          ...(col.lineType === "formula" && { formula: col.formula || [] }),
        })),
      };

      if (hasCellData) {
        tableObj.tableType = "content";
        tableObj.table = it.tableData || it.table || [];
      }

      return tableObj;
    }
    return it;
  });

  return { name, data };
};

const removeIdsAndFormulas = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map((item) => removeIdsAndFormulas(item));
  }

  if (typeof obj === "object" && obj !== null) {
    const newObj = {};

    Object.keys(obj).forEach((key) => {
      if (key === "_id" || key === "formula") {
        return;
      }

      newObj[key] = removeIdsAndFormulas(obj[key]);
    });

    return newObj;
  }

  return obj;
};

export default function Socie({
  tempId,
  setHandleSave,
  handleSave,
  isReadOnly = false,
}) {
  const [name, setName] = useState("");
  const [items, setItems] = useState(() => normalizeFromInitial(null));
  const [output, setOutput] = useState(null);
  const [forceOpenTableId, setForceOpenTableId] = useState(null);

  const [libraryName, setLibraryName] = useState("ST Library");
  const [activeItem, setActiveItem] = useState(null);
  const [selNote, setSelNote] = useState(null);
  const [selNoteId, setSelNoteId] = useState(null);
  const [selParentId, setSelParentId] = useState(null); 
  const [notes, setNotes] = useState(null);
  const [note, setNote] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const [isComposing, setIsComposing] = useState(false);
  const [noteName, setNoteName] = useState("");
  const inputRef = useRef(null);

  const [templateData, setTemplateData] = useState(null);

  // Cell linking and side panel state
  const [linkedCells, setLinkedCells] = useState({});
  const [activeCellKey, setActiveCellKey] = useState(null);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null);
  const [selectedDataID, setSelectedDataID] = useState(null);

  const localAdditionsRef = useRef({});
  const deletedKeysRef = useRef(new Set());

  useEffect(() => {
    if (!templateData?.socie) return;
    const map = {};
    templateData.socie.forEach((noteItem) => {
      const allNotes = [noteItem, ...((noteItem.subNotes || []))];
      allNotes.forEach((n) => {
        (n.data || []).forEach((block) => {
          if (block.dataType !== "table") return;
          const rowIndexMap = new Map(
            (block.tableRow || []).map((row, index) => [
              String(row?.name || "").trim(),
              index + 1,
            ])
          );
          const colIndexMap = new Map(
            (block.tableColumn || []).map((col, index) => [
              String(col?.name || "").trim(),
              index + 1,
            ])
          );

          (block.cellFormulas || []).forEach((f) => {
            const rowName = String(f?.row || "").trim();
            const colName = String(f?.col || "").trim();
            const apiKey = `${block._id}-${rowName}-${colName}`;

            map[apiKey] = f;

            const rowIndex = rowIndexMap.get(rowName);
            const colIndex = colIndexMap.get(colName);

            if (rowIndex && colIndex) {
              const localKey = `${block._id}-${rowIndex}-${colIndex}`;
              map[localKey] = f;
            }
          });
        });
      });
    });
    deletedKeysRef.current.forEach((k) => {
      delete map[k];
    });
    const merged = { ...localAdditionsRef.current, ...map };
    setLinkedCells(merged);
  }, [templateData]);

  useEffect(() => {
  console.log("ITEMS CHANGED", items);
}, [items]);

  const handleCellLinkClick = (row, col, dataID, tableItem) => {
    if (row === 0) return;
    if (col === 0) return;

    // Table generator uses the rendered header row, so SOCIE value columns are offset by one.
    const colName = String(
      tableItem?.tableColumn?.[col - 1]?.name ?? ""
    ).trim();

    if (!colName) return;

    const rowName = String(tableItem?.tableRow?.[row - 1]?.name ?? row);

    const cellKey = `${dataID}-${row}-${col}`;
    setActiveCellKey(cellKey);
    setSelectedCell({ row: rowName, col: colName });
    setSelectedDataID(dataID);
    setIsSidePanelOpen(true);
  };

  const getTemplateDetails = (updatedTemplate) => {
    if (updatedTemplate && updatedTemplate.socie) {
      setTemplateData(updatedTemplate);
      return;
    }
    getTemplateDetailsApiHandler({ groupingTemplateID: tempId })
      .then((response) => {
        if (response?.data?.success === true) {
          setTemplateData(response?.data?.groupingTemplate);
        }
      })
      .catch((error) => {});
  };
  useEffect(() => {
    getTemplateDetails();
  }, []);
  useEffect(() => {
    getTemplateDetails();
  }, [activeItem]);

  const [isAddingTable, setIsAddingTable] = useState(false);
  const handleAddParagraph = () => {
    setItems((prev) => [
      ...prev,
      {
        id: genId(),
        type: "text",
        text: [{ id: genId(), content: "" }],
        textFormat: "html",
        textRaw: " ",
        textPlain: " ",
      },
    ]);
  };

  const handleAddTable = () => {
    setIsAddingTable(true);
  };

  const removeItem = (id) => {
    const target = items.find((it) => it.id === id);
    if (!target) return;

    if (!templateData?._id || !selNoteId) {
      swal("Error", "Template data not loaded yet", "error");
      return;
    }

    setDeleteConfirm({
      title: "Are you sure?",
      message: "This item will be deleted permanently",
      onConfirm: () => {
        setDeleteConfirm(null);
        const filteredItems = items.filter((it) => it.id !== id);
        const payload = removeIdsAndFormulas(serialize(name, filteredItems));

        const apiCall =
          selNote === "general_sn"
            ? updateSubSocieApiHandler(
                {
                  groupingTemplateID: templateData._id,
                  socieID: selParentId,
                  subNoteID: selNoteId,
                },
                payload
              )
            : updateSocieApiHandler(
                { groupingTemplateID: templateData._id, socieID: selNoteId },
                payload
              );

        apiCall
          .then((response) => {
            if (response?.data?.success === true) {
              setItems(filteredItems);
              swal("Successfully deleted", "", "success");
              getTemplateDetails();
            } else {
              swal(
                "Error",
                response?.data?.error || "Unable to delete the item",
                "error"
              );
            }
          })
          .catch((error) => {
            swal(
              "Error",
              error?.response?.data?.error ||
                error?.message ||
                "Unable to delete the item",
              "error"
            );
          });
      }
    });
  };

  const updateEditorContent = (id, html) => {
    setItems((prev) =>
      prev.map((it) =>
        it.id === id
          ? {
              ...it,
              text: [{ id: it.text?.[0]?.id ?? genId(), content: html }],
              textFormat: "html",
              textRaw: html?.html ?? " ",
              textPlain: html?.text ?? " ",
            }
          : it
      )
    );
  };

    const updateTableItem = (id, patch) => {
      console.log("PATCH", patch);
      setItems((prev) =>
        prev.map((it) => (it.id === id ? { ...it, ...patch } : it))
      );
    };

  const handleAddSubRow = (tableId) => {
    if (!selNoteId) return;

    const tableItem = items.find((it) => it.id === tableId);
    const columns = tableItem?.tableColumn || [];

    const hasCellData =
      tableItem?.hasCellData !== undefined
        ? tableItem.hasCellData
        : (tableItem?.tableType === "content" || (Array.isArray(tableItem?.table) && tableItem.table.length > 0));

    const newRow = {
      isNewRow: true,
      isEdited: false,
      isSubRow: true,
    };

    if (columns.length > 0) {
      columns.forEach((col, idx) => {
        const key = col.name;
        if (key) {
          newRow[key] = idx === 0 ? "" : "0";
        }
      });
    } else {
      newRow["Particulars"] = "";
      newRow["Amount"] = "0";
    }

    setItems((prev) =>
      prev.map((it) => {
        if (it.id === tableId) {
          let insertIndex = -1;
          if (it.tableRow) {
            insertIndex = it.tableRow.findIndex((row) => {
              const name = (row?.name || "").trim().toLowerCase();
              return name === "total" || name.includes("balance at the end");
            });
          }

          const updatedTableRow = [...(it.tableRow || [])];
          const newTableRowItem = {
            name: "",
            lineType: "line item",
            isSubRow: true,
            isNewRow: true,
            isEdited: false,
          };

          if (insertIndex !== -1) {
            updatedTableRow.splice(insertIndex, 0, newTableRowItem);
          } else {
            updatedTableRow.push(newTableRowItem);
          }

          if (hasCellData) {
            const updatedTable = [...(it.table || [])];
            if (insertIndex !== -1) {
              updatedTable.splice(insertIndex, 0, newRow);
            } else {
              updatedTable.push(newRow);
            }
            return {
              ...it,
              table: updatedTable,
              tableRow: updatedTableRow,
            };
          } else {
            return {
              ...it,
              tableRow: updatedTableRow,
            };
          }
        }
        return it;
      })
    );
  };


  useEffect(() => {
    setOutput(serialize(name, items));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, items]);

  useEffect(() => {
    if (isComposing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isComposing]);

  const handleAddClick = () => {
    setIsComposing(true);
    setNoteName("");
  };

  const handleCancel = () => {
    setIsComposing(false);
    setNoteName("");
  };

  const handlePost = () => {
    const value = noteName.trim();
    if (!value) return;
    setNote([value, ...note]);
    setNoteName("");
    setIsComposing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handlePost();
    if (e.key === "Escape") handleCancel();
  };

  const [subComposeFor, setSubComposeFor] = useState(null);
  const [subNoteName, setSubNoteName] = useState("");
  const subInputRef = useRef(null);

  useEffect(() => {
    if (subComposeFor !== null && subInputRef.current) {
      subInputRef.current.focus();
    }
  }, [subComposeFor]);

  const openSubComposer = (noteIndex) => {
    setSubComposeFor(noteIndex);
    setSubNoteName("");
  };

  const cancelSubComposer = () => {
    setSubComposeFor(null);
    setSubNoteName("");
  };

  const postSubnote = () => {
    const value = subNoteName.trim();
    if (!value || subComposeFor === null) return;
    setNotes((prev) => {
      const next = [...prev];
      const target = { ...next[subComposeFor] };
      const subNotes = Array.isArray(target.subNotes)
        ? [...target.subNotes]
        : [];
      subNotes.push({ name: value });
      target.subNotes = subNotes;
      next[subComposeFor] = target;
      return next;
    });
    setSubNoteName("");
    setSubComposeFor(null);
  };

  const addSub = (note_id) => {
    const value = subNoteName.trim();
    if (!value) return;

    const generalNotesPayload = {
      name: subNoteName,
    };
    createSubSocieApiHandler(
      { groupingTemplateID: tempId, socieID: note_id },
      generalNotesPayload
    )
      .then((response) => {
        if (response?.data?.success === true) {
          setSubNoteName("");
          setSubComposeFor(null);
          setNotes(response?.data?.updatedGroupingTemplate?.socie);
        }
      })
      .catch((error) => {});
  };

  const handleSubKeyDown = (e) => {
    if (e.key === "Enter") postSubnote();
    if (e.key === "Escape") cancelSubComposer();
  };

  const breadcrumbItems = [
    {
      label: "Template Library",
      link: `/preview`,
      className: `${commoncss.nav_cnt_color} clickable`,
    },
    { label: `${libraryName}`, link: "", className: "headerLable" },
  ];

  // SAVE handler
  useEffect(() => {
    if (handleSave === "socie") {
      setHandleSave(null);

      const newRowsToCreate = [];
      items.forEach((it) => {
        if (it.type === "table" && it.table) {
          const cols = it.tableColumn || [];
          const col1Name = cols[0]?.name || "Particulars";
          const col2Name = cols[1]?.name || "Amount";

          it.table.forEach((row) => {
            if (row.isNewRow === true) {
              const nameVal = row[col1Name] || row.name || "";
              const cyVal = row[col2Name] || row.currentYear || 0;
              const cleanCy = Number(String(cyVal).replace(/,/g, "")) || 0;

              newRowsToCreate.push({
                tableId: it.id,
                payload: {
                  name: nameVal,
                  currentYear: cleanCy,
                  previousYear: 0,
                  isSubRow: true,
                },
              });
            }
          });
        }
      });

      const finalizeSave = (freshPayload) => {
        const payload = freshPayload || removeIdsAndFormulas(output);

        if (selNote === "general_sn") {
          updateSubSocieApiHandler(
            {
              groupingTemplateID: templateData._id,
              socieID: selParentId,
              subNoteID: selNoteId,
            },
            payload
          )
            .then((response) => {
              if (response?.data?.success === true) {
                swal("Data saved successfully", "", "success");
                getTemplateDetails();
              } else {
                swal("Error", response?.data?.error || "Unable to save", "error");
              }
            })
            .catch((error) => {
              swal("Error", error?.response?.data?.error || "Unable to save", "error");
            });
        } else {
          updateSocieApiHandler(
            { groupingTemplateID: templateData._id, socieID: selNoteId },
            payload
          )
            .then((response) => {
              if (response?.data?.success === true) {
                swal("Data saved successfully", "", "success");
                getTemplateDetails();
              } else {
                swal("Error", response?.data?.error || "Unable to save", "error");
              }
            })
            .catch((error) => {
              swal("Error", error?.response?.data?.error || "Unable to save", "error");
            });
        }
      };

      if (newRowsToCreate.length > 0) {
        const createPromises = newRowsToCreate.map((item) =>
          createSubSocieApiHandler(
            { groupingTemplateID: templateData._id, socieID: selNoteId },
            { name: item.payload.name || "" }
          )
        );

        Promise.all(createPromises)
          .then((responses) => {
            const createdIds = [];
            responses.forEach((res) => {
              const socie = res?.data?.updatedGroupingTemplate?.socie;
              if (!socie) return;

              for (const note of socie) {
                if (note._id === selNoteId) {
                  const subs = note.subNotes || [];
                  const existingIds = (items || [])
                    .flatMap((it) => it.table || [])
                    .map((r) => r._id)
                    .filter(Boolean);
                  for (let i = subs.length - 1; i >= 0; i--) {
                    if (
                      !existingIds.includes(subs[i]._id) &&
                      !createdIds.includes(subs[i]._id)
                    ) {
                      createdIds.push(subs[i]._id);
                      break;
                    }
                  }
                  break;
                }
              }
            });

            let createdIndex = 0;
            const updatedItems = items.map((it) => {
              if (it.type !== "table") return it;

              return {
                ...it,
                table: (it.table || []).map((row) => {
                  if (row.isNewRow === true && createdIndex < createdIds.length) {
                    const realId = createdIds[createdIndex++];
                    const newRow = { ...row, _id: realId, isNewRow: false };
                    delete newRow.isNew;
                    return newRow;
                  }
                  return row;
                }),
              };
            });

            setItems(updatedItems);
            const freshPayload = removeIdsAndFormulas(serialize(name, updatedItems));
            finalizeSave(freshPayload);
          })
          .catch((error) => {
            swal("Error", "Unable to create new rows", "error");
          });
      } else {
        finalizeSave();
      }
    }
  }, [handleSave, items, selNote, selParentId, selNoteId, templateData, output, name]);

  useEffect(() => {
    setNotes(templateData?.socie);
  }, [templateData]);

  const addNotes = () => {
    const value = noteName.trim();
    if (!value) return;

    const generalNotesPayload = {
      name: noteName,
    };
    createSocieApiHandler({ groupingTemplateID: tempId }, generalNotesPayload)
      .then((response) => {
        if (response?.data?.success === true) {
          setNoteName("");
          setIsComposing(false);
          setNotes(response?.data?.updatedGroupingTemplate?.socie);
        }
      })
      .catch((error) => {});
  };
  useEffect(() => {
    setNotes(templateData?.socie);
  }, [templateData]);

  useEffect(() => {
    if (notes && notes.length > 0 && !activeItem) {
      const firstNote = notes[0];

      setItems(normalizeFromInitial(firstNote));
      setActiveItem("gs_0");
      setSelNote("general");
      setSelNoteId(firstNote._id);
      setSelParentId(null);
      setOutput(firstNote);
      setName(firstNote.name);
    }
  }, [notes, activeItem]);
  return (
    <>
      <motion.div
        initial={{ x: 500, y: 500, opacity: 0 }}
        animate={{ x: 0, y: 0, opacity: 1 }}
        exit={{ x: 0, y: 0, opacity: 0 }}
        transition={{ duration: 0.7, ease: "easeInOut" }}
        className={libcss.hierarchyLayout}
        style={{ border: "0px", height: "calc(100% - 45px)" , padding: 0, width: "100%", maxWidth: "100%", margin: 0, display: "block", boxSizing: "border-box"}}
      >
        <div className={libcss.dataDivs} style={{border: "none", paddingLeft: "20px", paddingRight: "20px", paddingTop: "10px", paddingBottom: "24px", height: "100%", marginTop: 0, boxSizing: "border-box", overflowY: "auto", overflowX: "hidden"}}>
          <div className={libcss.levelCatDiv} style={{ paddingLeft: 0, paddingRight: 0 }}>
            {notes && notes.length === 0 && (
              <div className={libcss.notesItemView}>No data</div>
            )}
            <div style={{ display: "grid", gap: 14 }}>
              {items.map((it, idx) => {
                if (it.type === "text") {
                  const html = it.text?.[0]?.content ?? " ";
                  return (
                    <div
                      key={it.id}
                      style={{
                        background: "#fff",
                        border: "1px solid #e3efe7",
                        borderRadius: 14,
                        padding: 12,
                        boxShadow: "0 1px 6px rgba(0,0,0,0.04)",
                      }}
                    >
                      <div style={{ marginTop: isReadOnly ? 35 : 10 }}>
                        {!isReadOnly && (
                          <DelIcon
                            className={libcss.textEditorDel}
                            onClick={() => removeItem(it.id)}
                          />
                        )}
                        <QuillReactEditor
                          value={html}
                          onChange={(content) => {
                            updateEditorContent(it.id, content);
                          }}
                          disabled={isReadOnly}
                        />
                      </div>
                    </div>
                  );
                }

                if (it.type === "table") {
                  return (
                    <div
                      key={it.id}
                      style={{
                        background: "#fff",
                        border: "1px solid #e3efe7",
                        borderRadius: 14,
                        padding: "10px 0px 8px 0px",
                        boxShadow: "0 1px 6px rgba(0,0,0,0.04)",
                        width: "100%",
                        boxSizing: "border-box",
                        overflow: "hidden",
                      }}
                      className={libcss.hideVerticalScrollbar}
                    >
                      <div style={{ width: "100%" }}>
                       <div className={libcss.custTable1} style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 0 }}>
                        <ControlledTableGenerator
                          isSocieTable={true} 
                          allowAddRow={true}
                          linkedCells={linkedCells}
                          tableId={it._id || it.id}
                          onCellLinkClick={(row, col) =>
                            handleCellLinkClick(row, col, it._id || it.id, it)
                          }
                          onDelete={() => removeItem(it.id)}
                          onAddRow={() => handleAddSubRow(it.id)}
                          onCellBlur={(r, c, val) => {
                            setHandleSave("socie");
                          }}
                          forceOpen={it.id === forceOpenTableId}
                          value={{
                            tableTitle: it.tableTitle,
                            tableRow: it.tableRow,
                            tableColumn: it.tableColumn,
                            tableType: it.tableType,
                            table: it.table,
                            hasCellData: it.hasCellData,
                          }}
                          onChange={(next) =>
                            updateTableItem(it.id, {
                              tableTitle: next.tableTitle,
                              tableRow: next.tableRow,
                              tableColumn: next.tableColumn,
                              hasCellData: next.hasCellData,
                              tableData: next.tableData,
                              tableType: next.tableType,
                              table: next.table,
                            })
                          }
                          onAfterOpen={() => {
                            if (forceOpenTableId === it.id)
                              setForceOpenTableId(null);
                          }}
                          disabled={isReadOnly}
                          allowReorder={true}
                          getRowPermissions={(label) => {
                            const l = (label || "").trim().toLowerCase();
                            const normalized = l.replace(/\s+/g, " ").trim();

                              const isLocked =
                                normalized.includes("balance at the beginning") ||
                                normalized.includes("restated balance") ||
                                normalized.includes("total comprehensive income") ||
                                normalized.includes("balance at the end") ||
                                normalized === "total";

                            if (isLocked) {
                              return {
                                allowEdit: false,
                                allowDelete: false,
                                allowRearrange: false,
                              };
                            }
                            return {
                              allowEdit: true,
                              allowDelete: true,
                              allowRearrange: true,
                            };
                          }}
                        />
                      </div>
                      </div>
                    </div>
                  );
                }

                return null;
              })}
            </div>

            {selNote && !isReadOnly && (
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  alignItems: "center",
                  padding: 12,
                  borderRadius: 12,
                  marginBottom: 12,
                }}
              >
                <CirclePlus width={37} />
                <AddEditor width={140} onClick={handleAddParagraph} />
                <AddTable width={100} onClick={handleAddTable} />
              </div>
            )}
            {isAddingTable && (
              <ControlledTableGenerator
                isSocieTable={true}
                allowAddRow={true}
                forceOpen={true}
                value={{}}
                onChange={(next) => {
                  const id = genId();
                  setItems((prev) => [
                    ...prev,
                    {
                      id,
                      type: "table",
                      tableTitle: next.tableTitle,
                      tableRow: next.tableRow,
                      tableColumn: next.tableColumn,
                      hasCellData: next.hasCellData,
                      tableData: next.tableData,
                    },
                  ]);
                  setIsAddingTable(false);
                }}
                onCancel={() => setIsAddingTable(false)}
                allowReorder={true}
                getRowPermissions={(label) => {
                  const l = (label || "").trim().toLowerCase();
                  const locked = [
                    "balance at the beginning of the previous reporting period",
                    "restated balance at the beginning of the previous reporting period",
                    "total comprehensive income for the previous year",
                    "balance at the end of the previous reporting period",
                    "total",
                  ];
                  if (locked.includes(l)) {
                    return {
                      allowEdit: false,
                      allowDelete: false,
                      allowRearrange: false,
                    };
                  }
                  return {
                    allowEdit: true,
                    allowDelete: true,
                    allowRearrange: true,
                  };
                }}
              />
            )}
          </div>
        </div>
      </motion.div>
      {isSidePanelOpen && (
        <SidePanel
          type="socie"
          tempId={tempId}
          isTemplate={true}
          groupingTemplateID={tempId}
          activeCellKey={activeCellKey}
          showLinkPanel={isSidePanelOpen}
          setShowLinkPanel={(val) => {
            setIsSidePanelOpen(val);
            if (!val) setSelectedCell(null);
          }}
          selectedCell={selectedCell}
          disclosureID={selNoteId}
          dataID={selectedDataID}
          table={(() => {
            if (!templateData?.socie || !selectedDataID) return null;
            const allNotes = templateData.socie.flatMap((n) => [
              n,
              ...(n.subNotes || []),
            ]);
            const matchedNote = allNotes.find((n) => n._id === selNoteId);
            return (matchedNote?.data || []).find((b) => b._id === selectedDataID) || null;
          })()}
          onApply={(data, updatedTemplate) => {
            const apiKey =
              selectedDataID && selectedCell?.row && selectedCell?.col
                ? `${selectedDataID}-${selectedCell.row}-${selectedCell.col}`
                : null;

            if (!data) {
              deletedKeysRef.current.add(activeCellKey);
              if (apiKey) deletedKeysRef.current.add(apiKey);
              delete localAdditionsRef.current[activeCellKey];
              if (apiKey) delete localAdditionsRef.current[apiKey];
            } else {
              deletedKeysRef.current.delete(activeCellKey);
              if (apiKey) deletedKeysRef.current.delete(apiKey);
              localAdditionsRef.current[activeCellKey] = data;
              if (apiKey) localAdditionsRef.current[apiKey] = data;
            }

            setLinkedCells((prev) => {
              const updated = { ...prev };
              if (!data) {
                delete updated[activeCellKey];
                if (apiKey) {
                  delete updated[apiKey];
                }
              } else {
                updated[activeCellKey] = data;
                if (apiKey) {
                  updated[apiKey] = data;
                }
              }
              return updated;
            });
            if (updatedTemplate) {
              setTemplateData(updatedTemplate);
            } else {
              getTemplateDetails();
            }
          }}
          onSaveSuccess={getTemplateDetails}
        />
      )}
      <DeletePopup
        isOpen={deleteConfirm !== null}
        title={deleteConfirm?.title}
        message={deleteConfirm?.message}
        onConfirm={deleteConfirm?.onConfirm}
        onCancel={() => setDeleteConfirm(null)}
      />
    </>
  );
}
